import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

export default function SEO({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  keywords,
  noindex = false,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bodhira.online';

    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update description
    if (description) {
      updateMetaTag('description', description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    }

    // Update canonical
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      
      const fullCanonical = canonical.startsWith('http') ? canonical : `${siteUrl}${canonical}`;
      linkElement.setAttribute('href', fullCanonical);
    }

    // Update Open Graph tags
    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (ogImage) {
      const fullImageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
      updateMetaTag('og:image', fullImageUrl, true);
    }

    if (canonical) {
      const fullUrl = canonical.startsWith('http') ? canonical : `${siteUrl}${canonical}`;
      updateMetaTag('og:url', fullUrl, true);
    }

    // Add or update JSON-LD
    if (jsonLd) {
      // Remove existing JSON-LD scripts added by this component
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo-component]');
      existingScripts.forEach(script => script.remove());

      // Add new JSON-LD
      const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      
      jsonLdArray.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-component', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    // Cleanup function
    return () => {
      // Remove JSON-LD scripts when component unmounts
      if (jsonLd) {
        const scripts = document.querySelectorAll('script[type="application/ld+json"][data-seo-component]');
        scripts.forEach(script => script.remove());
      }
    };
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, ogType, keywords, noindex, jsonLd]);

  return null;
}